// utils/tableHelpers.js

export const textFieldSx = {
  "& .MuiInputBase-root": {
    minHeight: "32px",
  },
  "& .MuiInputBase-input": {
    padding: "4px 8px",
    fontSize: "0.875rem",
  },
};

const HUNDREDTHS_PER_M2 = 100;

export const parseJSONSafe = (val) => {
  if (val === null || val === undefined || val === "" || val === "null") {
    return undefined;
  }
  try {
    let parsed = typeof val === "string" ? JSON.parse(val) : val;
    // Handle double-stringified JSON (common with form data)
    while (
      typeof parsed === "string" &&
      (parsed.startsWith("{") || parsed.startsWith("[")) &&
      parsed !== "null"
    ) {
      try {
        const nextParsed = JSON.parse(parsed);
        if (nextParsed === parsed) break; // Prevent infinite loop
        parsed = nextParsed;
      } catch {
        break;
      }
    }
    return parsed;
  } catch {
    return undefined;
  }
};

export const formatCoords = (coords) => {
  if (coords == null || coords === "" || coords === "null") {
    return "";
  }

  const parsed = parseJSONSafe(coords);

  if (
    parsed &&
    typeof parsed === "object" &&
    parsed.lat != null &&
    parsed.lon != null
  ) {
    return `${parsed.lat}, ${parsed.lon}`;
  }

  if (typeof parsed === "string") {
    return parsed;
  }

  return "";
};

export const handleCoordinateChange = (value, updateCallback) => {
  // Keep the input as plain text while the user is typing.
  updateCallback(value);
};

export const handleCoordinateBlur = (value, updateCallback) => {
  const trimmedValue = value?.trim() ?? "";

  if (!trimmedValue || trimmedValue === "null") {
    updateCallback("");
    return;
  }

  const parts = trimmedValue.split(",").map((part) => part.trim());

  if (parts.length === 2 && parts[0] !== "" && parts[1] !== "") {
    const lat = Number(parts[0]);
    const lon = Number(parts[1]);

    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      updateCallback(`${lat}, ${lon}`);
      return;
    }
  }

  updateCallback("");
};

export const coordsToJSON = (value) => {
  if (value == null || value === "" || value === "null") {
    return null;
  }

  const parsed = parseJSONSafe(value);
  if (
    parsed &&
    typeof parsed === "object" &&
    parsed.lat != null &&
    parsed.lon != null
  ) {
    return JSON.stringify({
      lat: Number(parsed.lat),
      lon: Number(parsed.lon),
    });
  }

  const coordinateText = typeof parsed === "string" ? parsed : value;
  const parts = String(coordinateText)
    .trim()
    .split(",")
    .map((part) => part.trim());

  if (parts.length !== 2) {
    return null;
  }

  const lat = Number(parts[0]);
  const lon = Number(parts[1]);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return JSON.stringify({ lat, lon });
};

export const hundredthsToMeters = (n) => {
  if (n === null || n === undefined || n === "") return undefined;
  const asNumber = Number(n);
  if (!Number.isFinite(asNumber)) return undefined;
  return asNumber / HUNDREDTHS_PER_M2;
};

export const metersToHundredths = (n) => {
  if (n === null || n === undefined || n === "") return undefined;
  const asNumber = Number(n);
  if (!Number.isFinite(asNumber)) return undefined;
  return Math.round(asNumber * HUNDREDTHS_PER_M2);
};

export const formatAreaInput = (n) => {
  const meters = hundredthsToMeters(n);
  if (meters === undefined) return "";
  return Number(meters.toFixed(2)).toString();
};

export const formatArea = (n) => {
  const meters = hundredthsToMeters(n);
  if (meters === undefined) return "-";
  return `${Number(meters.toFixed(2))} m²`;
};

export const formatDate = (d) => {
  if (!d) return "-";
  try {
    const dt = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
    if (Number.isNaN(dt.getTime())) return "-";
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(dt);
  } catch {
    return "-";
  }
};

export const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
};

export const toFloat = (v) => {
  const f = parseFloat(v);
  return Number.isNaN(f) ? undefined : f;
};

export const groupBy = (arr, key) => {
  if (!Array.isArray(arr)) return {};
  return arr.reduce((acc, item) => {
    const k = item?.[key];
    if (k == null) return acc;
    (acc[k] ||= []).push(item);
    return acc;
  }, {});
};
